import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { paymentAPI } from '../../api/axios';

const SUPPORT_EMAIL = 'support@tuplecamp.com';

const CheckoutModal = () => {
  const { isCheckoutModalOpen, closeCheckout, selectedProduct } = useAuth();

  const [step, setStep] = useState('details'); // details | processing | success | error
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState('');

  // Reset when modal opens
  useEffect(() => {
    if (isCheckoutModalOpen) {
      setStep('details');
      setError('');
      setOrderId('');
      setEmail('');
      setPhone('');
      setName('');
    }
  }, [isCheckoutModalOpen]);

  if (!isCheckoutModalOpen || !selectedProduct) return null;

  const handleClose = () => {
    if (step === 'processing') return; // Don't close during payment
    closeCheckout();
    setStep('details');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate name
      if (!name.trim()) {
        throw new Error('Please enter your name');
      }

      // Validate phone (optional but if provided, should be valid)
      if (phone && !/^\d{10}$/.test(phone)) {
        throw new Error('Please enter a valid 10-digit phone number');
      }

      // Create order
      const { data } = await paymentAPI.createOrder(
        selectedProduct.id,
        email,
        phone,
        name
      );

      if (!data.success) {
        throw new Error(data.error || 'Failed to create order');
      }

      const orderData = data.data;
      setOrderId(orderData.orderId);
      setStep('processing');

      // Track if payment succeeded to prevent showing error after success
      let paymentSucceeded = false;

      // Configure Razorpay options
      const options = {
        key: orderData.keyId,
        amount: orderData.amount * 100, // Razorpay expects paise
        currency: orderData.currency,
        name: 'Tuple Camp',
        description: orderData.productName,
        order_id: orderData.razorpayOrderId,
        prefill: {
          name: orderData.customerName,
          email: orderData.customerEmail,
          contact: orderData.customerPhone
        },
        theme: {
          color: '#5624d0'
        },
        handler: async function (response) {
          // Mark as succeeded immediately to prevent error flash
          paymentSucceeded = true;

          // Payment successful, verify on backend
          try {
            const verifyResponse = await paymentAPI.verifyPayment(
              orderData.orderId,
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );

            if (verifyResponse.data.success) {
              setStep('success');
            } else {
              setStep('error');
              setError('Payment verification failed. Please contact support.');
            }
          } catch (verifyError) {
            console.error('Verification error:', verifyError);
            setStep('error');
            setError(verifyError.response?.data?.error || 'Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: function () {
            setStep('details');
            setLoading(false);
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);

      razorpay.on('payment.failed', function (response) {
        // Only log failed attempts - user can retry with different method
        // Don't show error state as Razorpay allows retrying
        if (!paymentSucceeded) {
          console.log('Payment attempt failed, user can retry:', response.error.description);
        }
      });
      razorpay.open();

    } catch (err) {
      console.error('Checkout error:', err);
      let errorMessage = 'Something went wrong. Please try again.';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      if (err.code === 'ERR_NETWORK') {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      }
      setError(errorMessage);
      setStep('details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content checkout-modal" onClick={(e) => e.stopPropagation()}>
        {step !== 'processing' && (
          <button className="modal-close" onClick={handleClose}>
            &times;
          </button>
        )}

        {step === 'details' && (
          <>
            <h2 className="modal-title">Complete Your Purchase</h2>

            <div className="product-summary">
              <div className="product-info">
                <h3>{selectedProduct.name}</h3>
                <div className="product-price-tag">
                  <span className="original-price">₹{selectedProduct.price * 10}</span>
                  <span className="current-price">
                    <span className="currency">₹</span>
                    <span className="amount">{selectedProduct.price}</span>
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="input-group">
                <label htmlFor="checkout-name">Name *</label>
                <input
                  type="text"
                  id="checkout-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>

              <div className="input-group">
                <label htmlFor="checkout-email">Email Address *</label>
                <input
                  type="email"
                  id="checkout-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
                <small className="input-hint">PDF will be sent to this email</small>
              </div>

              <div className="input-group">
                <label htmlFor="checkout-phone">Phone Number (optional)</label>
                <input
                  type="tel"
                  id="checkout-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="9999999999"
                  maxLength={10}
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading || !email || !name}
              >
                {loading ? 'Processing...' : `Pay ₹${selectedProduct.price}`}
              </button>

              <p className="secure-text">
                🔒 Secured by Razorpay
              </p>
            </form>
          </>
        )}

        {step === 'processing' && (
          <div className="processing-state">
            <div className="spinner"></div>
            <h2 className="modal-title">Processing Payment</h2>
            <p className="modal-subtitle">Please complete the payment in the popup window</p>
          </div>
        )}

        {step === 'success' && (
          <div className="success-state">
            <div className="success-icon">✓</div>
            <h2 className="modal-title">Payment Successful!</h2>
            <p className="modal-subtitle">
              Thank you for purchasing {selectedProduct.name}!
            </p>
            <div className="success-info">
              <p>We've sent the PDF to:</p>
              <p className="email-highlight">{email}</p>
              <p className="check-email-note">Please check your inbox (and spam folder) for the download link.</p>
            </div>
            <button className="btn btn-primary btn-full" onClick={handleClose}>
              Done
            </button>
            <p className="support-note">
              Need help? Contact us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
          </div>
        )}

        {step === 'error' && (
          <div className="error-state">
            <div className="error-icon">✕</div>
            <h2 className="modal-title">Payment Failed</h2>
            <p className="modal-subtitle">{error}</p>
            {orderId && (
              <p className="order-id-text">Order ID: {orderId}</p>
            )}
            <button
              className="btn btn-primary btn-full"
              onClick={() => setStep('details')}
            >
              Try Again
            </button>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="link-btn">
              Contact Support
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;
